import { Accordion as ArkAccordion, type UseAccordionProps } from "@ark-ui/react";
import type React from "react";
import { twJoin } from "tailwind-merge";

import { ChevronDownIcon } from "@/components/Icons/ChevronDownIcon";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";

type AccordionRootProps = UseAccordionProps & {
  children: React.ReactNode;
  className?: string;
};

const AccordionRoot = ({ children, className, ...props }: AccordionRootProps): React.ReactNode => {
  return (
    <ArkAccordion.Root multiple collapsible className={twJoin("w-full", className)} {...props}>
      {children}
    </ArkAccordion.Root>
  );
};

type AccordionItemProps = React.ComponentProps<typeof ArkAccordion.Item> & {
  children: React.ReactNode;
  className?: string;
};

const AccordionItem = ({ children, className, value, ...props }: AccordionItemProps): React.ReactNode => {
  return (
    <ArkAccordion.Item value={value} className={twJoin("mb-2 last:mb-0", className)} {...props}>
      {children}
    </ArkAccordion.Item>
  );
};

type AccordionTriggerProps = React.ComponentProps<typeof ArkAccordion.ItemTrigger> & {
  isNested?: boolean;
  children: React.ReactNode;
  className?: string;
};

const AccordionTrigger = ({ children, className, isNested, ...props }: AccordionTriggerProps): React.ReactNode => {
  return (
    <ArkAccordion.ItemTrigger
      className={twJoin(
        "group flex h-10 w-full items-center justify-start overflow-hidden rounded-sm border-2 border-transparent px-2 focus:outline-none focus-visible:border-grey-900 data-[state=open]:bg-grey-100 data-[state=open]:text-aop-basic-blue-500 hover:bg-grey-100",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon className="hidden shrink-0 group-data-[state=open]:block" />
      <ChevronRightIcon className="block shrink-0 group-data-[state=open]:hidden" />
      <span className={twJoin("pl-2", isNested ? "text-body" : "text-body-bold")}>{children}</span>
    </ArkAccordion.ItemTrigger>
  );
};

type AccordionContentProps = React.ComponentProps<typeof ArkAccordion.ItemContent> & {
  children: React.ReactNode;
  className?: string;
};

const AccordionContent = ({ children, className, ...props }: AccordionContentProps): React.ReactNode => {
  return (
    <ArkAccordion.ItemContent
      className={twJoin("py-3 data-[state=closed]:bg-grey-100 data-[state=open]:bg-transparent", className)}
      {...props}
    >
      {children}
    </ArkAccordion.ItemContent>
  );
};

export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
};
